import { Loader, Stack, Text } from '@hotjar/hotsauce';
import { capitalize } from '@hotjar/insights-common/lib/string';
import type { FeatureGroup } from '@hotjar/webapp/domains/revenue/plan/types';
import { BASIC, PLUS } from '@hotjar/webapp/domains/revenue/plan/feature-group';
import { useEffect, useState } from 'react';

import { useTrafficCoverage } from '@hotjar/webapp/hooks/useTrafficCoverage';
import { useTranslation } from '@hotjar/translations';
import type { Site } from '@hotjar/site';
import type { Product } from '@hotjar/webapp/domains/common/product/types';
import { ProductType } from '@hotjar/webapp/domains/common/product/types';
import { SkuDropdown } from '@hotjar/webapp/features/Billing/MultiSkuDropdown/SkuDropdown';
import { useProductPricing } from '@hotjar/webapp/features/Billing/Upsell/hooks/useProductPricing';
import { PlanPrice } from '@hotjar/webapp/features/Billing/Upsell/components/PlanPrice/PlanPrice';
import { getProductAllowance } from '@hotjar/webapp/domains/common/product/utils';
import type { BillingPeriod } from '@hotjar/subscription-management';
import { useErrorHandler } from '@hotjar/error-handler';
import { useEffectOnceWithDependencies } from '@hotjar/webapp/hooks/useEffectOnceWithDependencies';

import { PlanSelectionItemStyles as S } from './PlanSelectionItem.styles';
import { SecondaryText } from './components/SecondaryText/SecondaryText';
import { ObserveAllowance } from './components/ObserveAllowance/ObserveAllowance';
import { AskAllowance } from './components/AskAllowance/AskAllowance';

type PlanSelectionItemProps = {
  site: Site;
  isLowerTier: boolean;
  tier: FeatureGroup;
  tierProducts: Product[];
  recommendedProduct: Product | null;
  selectedProduct: Product;
  currentProduct: Product;
  productType: ProductType;
  showFeaturesList: boolean;
  changeSelectedProduct: (product: Product) => void;
  billingPeriod: BillingPeriod;
};

/**
 * This component renders a Tier column for a specific product type
 * It saves the tierSelectedProduct which is the pre defined selected plan (that can be used in the SkuDropdown component) if the associated RadioButton is selected
 * If the associated Radio Button is selected, it propagates the tierSelectedProduct state to the global state
 */
export const PlanSelectionItem = ({
  site,
  isLowerTier,
  tier,
  tierProducts,
  recommendedProduct,
  selectedProduct,
  currentProduct,
  showFeaturesList,
  productType,
  changeSelectedProduct,
  billingPeriod,
}: PlanSelectionItemProps): JSX.Element => {
  const handleError = useErrorHandler();

  const checkboxId = `${tier}-radio`;
  const featureGroupHasMultiplePlans = tierProducts.length > 1;
  const canSelfServe = tierProducts.some((product) => product.selfServeEnabled);
  const isChecked = selectedProduct.tier === tier;
  const isPlusOrBasic = currentProduct.tier === BASIC || currentProduct.tier === PLUS;
  const isCurrentProduct =
    currentProduct.tier === tier && (isPlusOrBasic || currentProduct.sku === selectedProduct.sku);

  const [tierSelectedProduct, setTierSelectedProduct] = useState<Product>(
    selectedProduct.tier === tier && selectedProduct.type === productType
      ? selectedProduct
      : tierProducts[0],
  );

  const selectedProductIsSelfServable = tierSelectedProduct.selfServeEnabled;

  // Update dropdown selection if productType is changed
  useEffect(() => {
    setTierSelectedProduct(
      selectedProduct.type === productType && selectedProduct.tier === tier
        ? selectedProduct
        : tierProducts[0],
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productType]);

  const isRecommendedPlan = recommendedProduct?.sku === tierSelectedProduct.sku;

  const { t } = useTranslation('billing', { keyPrefix: 'plan-select-upsell-modal.plan-select' });

  const { coveragePercentage } = useTrafficCoverage(
    undefined,
    getProductAllowance(tierSelectedProduct),
  );

  const {
    isLoading: isLoadingPrice,
    price,
    symbol,
    isError: isErrorProductPricing,
    error: errorProductPricing,
  } = useProductPricing({
    product: tierSelectedProduct,
    site,
    billingPeriod,
  });

  const onChangeSelectedTier = () => {
    changeSelectedProduct(tierSelectedProduct);
  };

  const onSkuDropdownChange = (product: Product) => {
    setTierSelectedProduct(product);
    changeSelectedProduct(product);
  };

  useEffectOnceWithDependencies(
    () => {
      if (errorProductPricing && errorProductPricing instanceof Error) {
        handleError(errorProductPricing);
      }
    },
    [isErrorProductPricing, errorProductPricing],
    () => isErrorProductPricing,
  );

  return (
    <S.PlanSelectionItem data-testid={`plan-selection-item-${tier}`}>
      <Stack direction="column" spacing="x2">
        <S.Radio disabled={isLowerTier}>
          <S.RadioLabel
            htmlFor={checkboxId}
            selected={isChecked}
            tall={billingPeriod === 'ANNUALLY'}
          >
            <S.RadioInput
              isDisabled={isLowerTier}
              id={checkboxId}
              isChecked={isChecked}
              onChange={onChangeSelectedTier}
            />
            <SecondaryText
              isLowerTier={isLowerTier}
              isRecommendedPlan={isRecommendedPlan}
              isCurrentProduct={isCurrentProduct}
              productType={productType}
            />
            <S.RadioMainInfo>
              <Text style={{ color: 'inherit' }} textType="header5" as="h2">
                {capitalize(tier)}
              </Text>
              <Loader isLoading={isLoadingPrice} size="m" isGreyscale>
                {selectedProductIsSelfServable && price ? (
                  <PlanPrice symbol={symbol} price={price} billingPeriod={billingPeriod} />
                ) : (
                  <Text textType="subtitle2">{t('book-a-call')}</Text>
                )}
              </Loader>
            </S.RadioMainInfo>
          </S.RadioLabel>
        </S.Radio>

        <div>
          {!canSelfServe && (
            <S.Allowance>
              <S.FixedHeightAllowanceText>
                {productType === ProductType.Analytics && t('allowances.unlimited-sessions')}
                {productType === ProductType.Feedback && t('allowances.unlimited-responses')}
              </S.FixedHeightAllowanceText>
              <S.FixedHeightCoverageWrapper>&nbsp;</S.FixedHeightCoverageWrapper>
            </S.Allowance>
          )}

          {!featureGroupHasMultiplePlans && canSelfServe && (
            <S.Allowance>
              {productType === ProductType.Analytics && (
                <ObserveAllowance
                  coveragePercentage={coveragePercentage}
                  allowances={getProductAllowance(tierSelectedProduct)}
                />
              )}
              {productType === ProductType.Feedback && (
                <AskAllowance
                  allowances={getProductAllowance(tierSelectedProduct)}
                  isRecommendedPlan={isRecommendedPlan}
                />
              )}
            </S.Allowance>
          )}

          {featureGroupHasMultiplePlans && canSelfServe && (
            <Stack direction="column" spacing="x1">
              <SkuDropdown
                onChange={onSkuDropdownChange}
                products={tierProducts}
                activeProduct={tierSelectedProduct}
                allowBookSalesCall={tier === 'scale'}
                productType={
                  tierSelectedProduct.type === ProductType.Analytics
                    ? 'analyticsProducts'
                    : 'feedbackProducts'
                }
                hidePlanBadges
                showTitle={false}
                siteAccount={site.account}
                currentProduct={currentProduct}
                disableLowerTierProducts
              />
              <>
                {productType === ProductType.Analytics && (
                  <ObserveAllowance
                    coveragePercentage={coveragePercentage}
                    allowances={getProductAllowance(tierSelectedProduct)}
                    withSkuDropdown
                  />
                )}
                {productType === ProductType.Feedback && (
                  <AskAllowance
                    allowances={getProductAllowance(tierSelectedProduct)}
                    withSkuDropdown
                    isRecommendedPlan={isRecommendedPlan}
                  />
                )}
              </>
            </Stack>
          )}
        </div>

        {showFeaturesList && (
          <>
            <S.PlanFeaturesDivider />
            <S.StyledPlanFeatures
              productType={productType}
              tier={tier}
              tierProducts={tierProducts}
              selectedPlan={tierSelectedProduct}
            />
          </>
        )}
      </Stack>
    </S.PlanSelectionItem>
  );
};
